<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import {
    computed,
    inject,
    onMounted,
    ref,
    watchEffect,
    type ComputedRef,
    type Ref,
  } from 'vue';
  import { useRoute, type RouteLocationRaw } from 'vue-router';
  import { useDisplay, useTheme } from 'vuetify';

  import { useMainPageRoute } from '@/composables/useMainPageRoute.composable';
  import { useI18 } from '@/plugins';
  import { useAuthStore } from '@/stores/auth.store';
  import { useLocaleStore } from '@/stores/locale.store';
  import { useOrganizationStore } from '@/stores/organzation.pinia';
  import type { headerItem } from '@/submodules/himam-common-components/src/helpers/interfaces/header-item.interface';

  import ToggleTheme from '../../../ToggleTheme.vue';
  import CartIcon from '../CartIcon.vue';
  import FavoriteIcon from '../FavoriteIcon.vue';
  import LanguageToggle from '../LanguageToggle.vue';
  import LoginAndRegister from '../LoginAndRegister.vue';
  import NotificationIcon from '../notification/NotificationIcon.vue';
  import ProfileIcon from '../ProfileIcon.vue';

  import MenuDrawer from './drawer/MenuDrawer.vue';
  import SearchField from './HeaderSearchField.vue';

  const organizationStore = useOrganizationStore();
  const { organization, marketplaceEnabled } = storeToRefs(organizationStore);
  const path = computed(() => {
    return route.path == '/' ? window.location.pathname : route.path;
  });

  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);

  const emit = defineEmits<{
    (event: 'update', value: boolean): void;
  }>();

  const route = useRoute();
  const isLoggedIn = computed(() => !!user.value?.id);
  const showCartIcon = computed(() => marketplaceEnabled.value);
  const showOnlyLogo = computed(
    () => path.value.startsWith('/assessments') ?? route.meta?.showOnlyLogo
  );

  const homePage = useMainPageRoute;
  const openMenu = ref(false);
  const appBar = ref(false);
  const activeItem: Ref<RouteLocationRaw[]> = ref([]);
  const headerItems: ComputedRef<headerItem[]> | undefined =
    inject('headerItems');
  const activeRouteName = computed(() => route.meta.activeRouteName);
  watchEffect(() => {
    let firstMath = '';
    headerItems?.value.forEach((item) => {
      if (!item.children) {
        if (item.routeName === activeRouteName.value) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
          return;
        }
        if (
          path.value.startsWith(item.to as string) &&
          (item.to as string)?.length >= firstMath.length
        ) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
        }
      } else {
        item.children.forEach((child) => {
          if (item.routeName === activeRouteName.value) {
            firstMath = item.to as string;
            activeItem.value = [item.to as string];
            return;
          }
          if (
            path.value.startsWith(child.to as string) &&
            (child.to as string)?.length >= firstMath.length
          ) {
            firstMath = child.to as string;
            activeItem.value = [child.to as string];
          }
        });
      }
    });
    if (!firstMath.length) activeItem.value = [];
  });
  const isAuthPage = computed(() => {
    return (
      path.value.startsWith('/auth') ||
      path.value.startsWith('/register') ||
      !user.value
    );
  });

  const { mdAndUp, lgAndUp } = useDisplay();
  const { t } = useI18();
  const localeStore = useLocaleStore();
  const { isLTR } = storeToRefs(localeStore);
  const theme = useTheme();
  onMounted(() => {
    const current = localStorage.getItem('theme');
    theme.global.name.value =
      current === 'myCustomDarkTheme'
        ? 'myCustomDarkTheme'
        : 'myCustomLightTheme';
  });
</script>

<template>
  <v-app-bar
    v-if="showOnlyLogo"
    elevation="0"
    height="60"
    class="pa-4 px-sm-5 py-md-8 flex-grow-0 justify-center bg-transparent"
    :absolute="true"
    @update:model-value="emit('update', appBar)"
  >
    <div class="flex flex-column ga-1 mx-auto">
      <img
        :src="organization?.logo?.url"
        alt="Expert Logo"
        contain
        height="60px"
        :width="undefined"
        class="mx-auto d-block"
      />
      <a
        v-if="organization?.parentOrganization"
        target="_blank"
        :href="`https://${organization.parentOrganization.studentUrl}`"
        class="mx-auto mx-md-0"
      >
        <div
          class="text-caption text-outline"
          v-html="
            t('shared.poweredBy', {
              organization: isLTR
                ? organization.parentOrganization.name
                : (organization.parentOrganization.nameArabic ??
                  organization.parentOrganization.name),
            })
          "
        ></div>
      </a>
    </div>
  </v-app-bar>
  <MenuDrawer
    id="menu-drawer-items-opening"
    :open-menu="openMenu"
    @update:open-menu="(val) => (openMenu = val)"
  >
  </MenuDrawer>
  <v-app-bar
    v-if="!showOnlyLogo"
    v-model="appBar"
    :absolute="false"
    scroll-behavior="hide"
    :scroll-threshold="243"
    :height="mdAndUp ? '104' : '72'"
    elevation="0"
    :class="lgAndUp ? 'app-bar-header' : ''"
    class="px-4 px-sm-5"
    style="border-bottom: 0.5px solid #e0e0e0"
    @update:model-value="emit('update', appBar)"
  >
    <template v-if="!mdAndUp" #prepend>
      <v-app-bar-nav-icon
        aria-label="open or close menu"
        density="compact"
        class="d-block d-md-none"
        @click.prevent="openMenu = !openMenu"
      ></v-app-bar-nav-icon>
    </template>
    <v-app-bar-title class="ma-0">
      <div class="d-flex align-center ga-4 ga-md -8">
        <div class="flex flex-column ga-1">
          <RouterLink :to="{ name: homePage.name }" class="mx-auto mx-md-0">
            <img
              :src="organization?.logo?.url"
              alt="Expert Logo"
              cover
              height="35"
              class="my-auto d-block"
            />
          </RouterLink>
          <a
            v-if="organization?.parentOrganization"
            target="_blank"
            :href="`https://${organization.parentOrganization.studentUrl}`"
            class="mx-auto mx-md-0"
          >
            <div
              class="text-caption text-outline"
              v-html="
                t('shared.poweredBy', {
                  organization: isLTR
                    ? organization.parentOrganization.name
                    : (organization.parentOrganization.nameArabic ??
                      organization.parentOrganization.name),
                })
              "
            ></div>
          </a>
        </div>
        <v-tabs class="align-center d-none d-md-block" hide-slider>
          <v-tab
            v-for="(item, i) in headerItems"
            :key="item.title"
            :to="item.to"
            class="pa-1 app-bar-tab"
            min-width="0"
            :class="[
              i != (headerItems?.length || 0) - 1 ? 'me-2 me-sm-4 me-md-8' : '',
            ]"
          >
            <span
              :class="[
                activeItem?.[0] === item.to
                  ? 'text-black active-tab'
                  : 'text-gray',
              ]"
              class="position-relative"
            >
              {{ item.title }}
            </span>
          </v-tab>
        </v-tabs>
        <SearchField
          v-if="!isAuthPage && mdAndUp"
          :app-bar="appBar"
        ></SearchField>
      </div>
    </v-app-bar-title>
    <template #append>
      <div class="d-flex">
        <!--if user Authorized  -->
        <div v-if="isLoggedIn" class="d-flex ga-2 align-center">
          <FavoriteIcon v-if="mdAndUp" />
          <CartIcon v-if="showCartIcon" />
          <v-divider v-if="mdAndUp" vertical color="primary"></v-divider>
          <NotificationIcon> </NotificationIcon>
          <ProfileIcon />
        </div>
        <div v-else class="ga-2 d-none d-md-flex">
          <ToggleTheme :show-as-menu="false" />
          <LanguageToggle />
          <LoginAndRegister />
        </div>
      </div>
    </template>
  </v-app-bar>
</template>
<style lang="scss">
  .app-bar-header {
    padding-inline: 108px !important;
  }
  .active-tab {
    border-bottom: 3px $secondary solid;
  }
  .beta-chip {
    top: 0;
    right: 0;
    max-width: none;
    transform: translate(50%, -100%);
  }
  .ar-beta-chip {
    top: 0;
    left: 0;
    max-width: none;
    transform: translate(-50%, -100%);
  }
  .app-bar-tab .v-btn__overlay {
    height: 40px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }
</style>
